import classNames from 'classnames'
import React, { createElement } from 'react'

import getConfig from 'next/config'
import CustomImage from 'src/components/Image'
import useI18n from 'src/hooks/use_i18n'
import { filterRegex } from 'src/utils/carriers_info'
import Styles from './index.module.scss'

const { publicRuntimeConfig } = getConfig()

const assetPrefix = publicRuntimeConfig.assetPrefix || ''

// #region component
interface Props {
  carriersFaqBlockData?: any
  carrierName?: any
  totalCarriersStr?: number | string
  blockType?: string
}

export default function SeoFaqBlock({ carriersFaqBlockData, carrierName, totalCarriersStr, blockType = '' }: Props) {
  const { i18nFormatAll } = useI18n()
  const renderTable = (answerItem: any, answerIndex: number) => (
    <React.Fragment key={`table-block-${answerIndex}`}>
      {answerItem.headerTextType && answerItem.headerTextType !== 'h2' && answerItem.headerText ? (
        createElement(answerItem.headerTextType, {
          id: filterRegex(answerItem.headerText, /[ ?]/g, '-'),
          key: answerItem.headerTextType + answerIndex,
          dangerouslySetInnerHTML: { __html: answerItem.headerText }
        })
      ) : answerItem.headerText ? (
        <h2
          key={'h2' + answerIndex}
          id={filterRegex(answerItem.headerText, /[ ?]/g, '-')}
          dangerouslySetInnerHTML={{
            __html: i18nFormatAll(answerItem.headerText, {
              carriersName: carrierName
            })
          }}
        ></h2>
      ) : null}
      {answerItem.descText && (
        <p
          key={answerItem.answerType + 'p' + answerIndex}
          dangerouslySetInnerHTML={{
            __html: i18nFormatAll(answerItem.descText, { carriersName: carrierName, carriersNum: totalCarriersStr })
          }}
        />
      )}
      <div className={classNames(Styles['statues-table'])} key={`statues-table-${answerIndex}`}>
        <table>
          <thead>
            <tr>
              {answerItem.headers?.map((header: any, index: number) => (
                <th
                  key={`table-th-${answerIndex}-${index}`}
                  rowSpan={header.rowSpan || 1}
                  colSpan={header.colSpan || 1}
                  dangerouslySetInnerHTML={{
                    __html: i18nFormatAll(header.content, { carriersName: carrierName, carriersNum: totalCarriersStr })
                  }}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {answerItem.rows?.map((row: any[], rowIndex: number) => (
              <tr key={`table-tr-${answerIndex}-${rowIndex}`}>
                {row.map((cell: any, cellIndex: number) => (
                  <td
                    key={`table-td-${answerIndex}-${rowIndex}-${cellIndex}`}
                    rowSpan={cell.rowSpan || 1}
                    colSpan={cell.colSpan || 1}
                    dangerouslySetInnerHTML={{
                      __html: i18nFormatAll(cell.content, { carriersName: carrierName, carriersNum: totalCarriersStr })
                    }}
                  />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </React.Fragment>
  )

  const answerItemDom = (answerItem: any, answerIndex: number) => {
    switch (answerItem.answerType) {
      case 'p':
        return answerItem.answerItem.map((item: string, index: number) => (
          <p
            key={`${answerItem.answerType}-${answerIndex}-${index}`}
            dangerouslySetInnerHTML={{
              __html: i18nFormatAll(item, {
                '0': totalCarriersStr,
                carriersName: carrierName,
                carriersNum: totalCarriersStr
              })
            }}
          />
        ))
      case 'ul':
        return (
          <ul key={`ul-${answerIndex}`}>
            {answerItem.answerItem.map((item: string, index: number) => (
              <li
                key={`ul-${answerIndex}-${index}`}
                dangerouslySetInnerHTML={{
                  __html: i18nFormatAll(item, { carriersName: carrierName, carriersNum: totalCarriersStr })
                }}
              />
            ))}
          </ul>
        )
      case 'ol':
        return (
          <ol key={`ol-${answerIndex}`}>
            {answerItem.answerItem.map((item: string, index: number) => (
              <li
                key={`ol-${answerIndex}-${index}`}
                dangerouslySetInnerHTML={{
                  __html: i18nFormatAll(item, { carriersName: carrierName, carriersNum: totalCarriersStr })
                }}
              />
            ))}
          </ol>
        )
      case 'img':
        return answerItem.answerItem.map((item: any, index: number) => (
          <div className="relative" key={`img-${answerIndex}-${index}`}>
            <CustomImage
              src={`${assetPrefix}/assets/images/carriers/carrier_info/${item.carrierName}/${item.imgUrl}`}
              loading="lazy"
              decoding="async"
              alt={item.alt}
              fill
              className={classNames('lazy-image-fill', item.class)}
            />
          </div>
        ))

      case 'table':
        return renderTable(answerItem, answerIndex)
      default:
        if (answerItem.answerType) {
          return answerItem.answerItem.map((item: string, index: number) =>
            createElement(answerItem.answerType, {
              key: answerItem.answerType + answerIndex + index,
              dangerouslySetInnerHTML: {
                __html: i18nFormatAll(item, {
                  '0': totalCarriersStr,
                  carriersName: carrierName,
                  carriersNum: totalCarriersStr
                })
              }
            })
          )
        } else {
          return null
        }
    }
  }

  if (!carriersFaqBlockData || !carriersFaqBlockData.length) {
    return null
  }

  return (
    <>
      {carriersFaqBlockData?.map((qaItem, listIndex) => {
        return (
          <div
            className={classNames(Styles['seoFaqBlock'])}
            key={listIndex}
            itemProp={qaItem.question ? 'mainEntity' : undefined}
            itemScope={!!qaItem.question}
            itemType={qaItem.question ? 'https://schema.org/Question' : undefined}
          >
            {qaItem.questionType && qaItem.questionType !== 'h2' && qaItem.question ? (
              createElement(qaItem.questionType, {
                id: filterRegex(qaItem.question, /[ ?]/g, '-'),
                key: `item-question-${listIndex}`,
                itemProp: 'name',
                dangerouslySetInnerHTML: { __html: qaItem.question }
              })
            ) : qaItem.question ? (
              <h2
                key={`item-question-${listIndex}`}
                id={filterRegex(qaItem.question, /[ ?]/g, '-')}
                itemProp="name"
                dangerouslySetInnerHTML={{
                  __html: i18nFormatAll(qaItem.question, {
                    carriersName: carrierName
                  })
                }}
              ></h2>
            ) : null}

            {!!qaItem?.answer?.length && (
              <div
                key={`answer-${listIndex}`}
                itemProp={qaItem.question ? 'acceptedAnswer' : undefined}
                itemScope={qaItem.question ? true : false}
                itemType={qaItem.question ? 'https://schema.org/Answer' : undefined}
              >
                <div itemProp={qaItem.question ? 'text' : undefined}>
                  {qaItem.answer.map((answerItem, answerIndex) => {
                    return (
                      <React.Fragment key={`answer-${listIndex}-${answerIndex}`}>
                        {answerItem.answerQuestion && (
                          <h3
                            key={`answerQuestion-${listIndex}-${answerIndex}`}
                            dangerouslySetInnerHTML={{
                              __html: i18nFormatAll(answerItem.answerQuestion, {
                                carriersName: carrierName
                              })
                            }}
                          ></h3>
                        )}
                        {answerItem.answerDescription && (
                          <h4
                            key={`answerDescription-${listIndex}-${answerIndex}`}
                            dangerouslySetInnerHTML={{
                              __html: i18nFormatAll(answerItem.answerDescription, {
                                carriersName: carrierName
                              })
                            }}
                          ></h4>
                        )}
                        {answerItemDom(answerItem, answerIndex)}
                      </React.Fragment>
                    )
                  })}
                </div>
              </div>
            )}

            {blockType == 'table' && renderTable(qaItem, listIndex)}
          </div>
        )
      })}
    </>
  )
}
